import { FieldArray, Formik } from 'formik';

import { CloseButton } from '@components/ActionButtons';
import { Box } from '@components/Box';
import { PrimaryButton } from '@components/Buttons/PrimaryButtons';
import {
  FormButtonsContainer,
  FormSectionContainer,
} from '@components/Container';
import { CancelButton, FormikDateInput, SubmitButton } from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { Space } from '@components/Space';
import { useNotification } from '@hooks/useNotification';
import { salesTaxDefaultValue, salesTaxValidation } from '@models/SalesTax';
import { SalesTaxService } from '@services/SalesTax';

interface Props {
  onClose?: (refreshGrid?: boolean) => void;
}

export const SalesTaxCreate: React.FC<Props> = ({ onClose }) => {
  const { showError, showSuccess } = useNotification();

  return (
    <>
      <CloseButton onClick={() => onClose && onClose(false)} />

      <Box size="medium">
        <Formik
          initialValues={salesTaxDefaultValue}
          validationSchema={salesTaxValidation}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={(values, actions) => {
            SalesTaxService.create(values)
              .then((result) => {
                if (result.isSuccess) {
                  showSuccess('Successfully created a record.');
                  onClose && onClose(true);
                } else {
                  console.error(
                    'Error: Failed to create a record.',
                    result.errorMessages
                  );
                  showError(`${result.errorMessages}`);
                }
              })
              .finally(() => {
                actions.setSubmitting(false);
              });
          }}
        >
          {(formikProps) => {
            return (
              <>
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    <FormikInput
                      label="Name"
                      name="name"
                      autoComplete="salestax-name"
                    />
                    <FormikInput label="Description" name="description" />
                    <FormikInput label="Abbreviation" name="abbreviation" />

                    <FieldArray
                      name="salesTaxRates"
                      render={(arrayHelpers) => (
                        <div className="flex flex-col items-stretch lg:space-y-4 md:space-y-3 space-y-2 content-start">
                          {formikProps.values.salesTaxRates &&
                            formikProps.values.salesTaxRates.map(
                              (rate, index) => (
                                <div
                                  key={index}
                                  className="flex flex-row items-start space-x-1.5"
                                >
                                  <FormikInput
                                    label="Rate (%)"
                                    name={`salesTaxRates[${index}].rate`}
                                    value={rate.rate}
                                  />
                                  <FormikDateInput
                                    label="Effective Date"
                                    name={`salesTaxRates[${index}].effectiveDate`}
                                    value={rate.effectiveDate}
                                  />

                                  <div className="pt-6">
                                    <PrimaryButton
                                      onClick={() => arrayHelpers.remove(index)}
                                    >
                                      -
                                    </PrimaryButton>
                                  </div>
                                </div>
                              )
                            )}

                          <PrimaryButton
                            onClick={() =>
                              arrayHelpers.push({ rate: '', effectiveDate: '' })
                            }
                          >
                            + Rate
                          </PrimaryButton>
                        </div>
                      )}
                    />

                    <Space />
                  </FormSectionContainer>
                  <FormButtonsContainer>
                    <CancelButton onClick={() => onClose && onClose(false)} />

                    <SubmitButton
                      label="Save"
                      disabled={
                        formikProps.isSubmitting || !formikProps.isValid
                      }
                    />
                  </FormButtonsContainer>
                </form>
              </>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};
